import React, {useRef, useState} from 'react';
import {observer} from "mobx-react";
import styled, {css} from "styled-components";
import {useServices} from "../../hooks/use-services.hook";
import {ISupportedLanguageViewModel} from "../../services/language/language.service.interface";
import {IonIcon} from "@ionic/react";
import {globeOutline} from "ionicons/icons";
import {ITooltipController, TooltipComponent, TooltipToggleBehaviorEnum} from "../tooltip/tooltip.component";

const LanguageSwitcherBox = styled.div`
    display: flex;
    cursor: pointer;
    color: ${props => props.theme.colors.primaryShade};
`

const CurrentLanguageBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2px;
    text-transform: uppercase;
    font-size: ${props => props.theme.fontSize.h4};
`

const DropDownItemsContainerBox = styled.div`
    display: flex;
    flex-direction: column;
    font-size: ${props => props.theme.fontSize.body1};
    color: ${props => props.theme.colors.dark};
    padding: ${props => props.theme.spacing.cardDefaultPadding};
`



const DropDownItemBox = styled.div<{$isPrevious: boolean}>`
    padding: ${props => props.theme.spacing.spacing20} ${props => props.theme.spacing.spacing48};
    border-bottom: 1px solid ${props => props.theme.border.mainColor};
    cursor: pointer;
    &:hover {
        background-color: ${props => props.theme.colors.lightShade};
        border-radius: ${props => props.theme.border.defaultRadius};
    }
    
    ${props => props.$isPrevious
            ? css`
                border-bottom-color: ${props => props.theme.colors.cardBackground};
            `
            : ''
    }
    
    &:last-of-type {
        border-bottom: none;
    }
`

interface DropDownItemComponentProps {
    index: number;
    currentHoverIndex: number;
    lang: ISupportedLanguageViewModel;
    onClick: (lang: ISupportedLanguageViewModel) => void;
    onHover: (index: number) => void;
}

const DropDownItemComponent: React.FC<DropDownItemComponentProps> = observer((props) => {

    return (
        <DropDownItemBox onClick={() => props.onClick(props.lang)}
                         $isPrevious={props.index === props.currentHoverIndex - 1}
                         onMouseOver={() => props.onHover(props.index)}>
            {props.lang.name}
        </DropDownItemBox>
    )
});

const DropDownItemsComponent: React.FC<{onLangSelected: (lang: ISupportedLanguageViewModel) => void}> = observer((props) => {
    const services = useServices();
    const [currentHoverIndex, setCurrentHoverIndex] = useState(-1);
    const supportedLanguages = services.language.supportedLanguages.filter(l => l.code !== services.language.currentLanguage);

    const renderOnLanguage = (lang: ISupportedLanguageViewModel, index: number) => {
        return (<DropDownItemComponent key={lang.code}
                                               index={index}
                                               currentHoverIndex={currentHoverIndex}
                                               lang={lang}
                                               onClick={props.onLangSelected}
                                               onHover={(idx) => setCurrentHoverIndex(idx)}/>);
    }

    return (
        <DropDownItemsContainerBox onMouseLeave={() => setCurrentHoverIndex(-1)}>
            {supportedLanguages.map(renderOnLanguage)}
        </DropDownItemsContainerBox>
    )
});

interface LanguageSwitcherComponentProps {
    className?: string;
}

export const LanguageSwitcherComponent: React.FC<LanguageSwitcherComponentProps> = observer((props) => {
    const services = useServices();
    const currentLangElementRef = useRef<HTMLDivElement | null>(null);
    const tooltipControllerRef = useRef<ITooltipController | null>(null);

    const onLangSelected = (lang: ISupportedLanguageViewModel) => {
        services.language.changeCurrentLanguage(lang.code);
        tooltipControllerRef.current?.close();
    }

    return (
        <LanguageSwitcherBox className={props.className}>
            <CurrentLanguageBox ref={currentLangElementRef}>
                <span>{services.language.currentLanguageIso2}</span>
                <IonIcon icon={globeOutline}/>
            </CurrentLanguageBox>
            <TooltipComponent targetRef={currentLangElementRef}
                              placement={"bottom-start"}
                              toggleBehavior={TooltipToggleBehaviorEnum.OnTargetClick}
                              tooltipControllerRef={tooltipControllerRef}
                              hideArrow={true}>
                <DropDownItemsComponent onLangSelected={onLangSelected}/>
            </TooltipComponent>
        </LanguageSwitcherBox>

    )
});